// components
import { Box } from '@mui/material';
import Autocomplete, {
  AutocompleteChangeReason,
  AutocompleteChangeDetails
} from '@mui/material/Autocomplete';
import { Icon } from '@sdb/planning-ui';

// react/libs
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';

// queries
import { mutations } from 'RestAPIs/rapini';

// types
import {
  FavoriteAutocompleteProps,
  FavoriteOptionProps,
  ShowMoreOption,
  NoSuggestionsOption,
  SuggestionsLabelOption,
  AutocompleteOption
} from './FavoriteAutocomplete.types';

// utils
import {
  getLabelProp,
  isShowMoreOption,
  isNoSuggestionsOption,
  isSuggestionsLabelOption,
  preFilterOptions,
  sortOptions,
  createFilterOptions
} from './FavoriteAutocomplete.utils';

// styles
import {
  OptionDisplayWrapper,
  OptionIconWrapper,
  OptionWrapper,
  RegularOption,
  ShowMoreOption as ShowMoreOptionStyle
} from './FavoriteAutocomplete.styles';

export default function FavoriteAutocomplete<T>({
  options,
  suggestedOptions = [],
  entityIdProperty = 'entityId',
  idProperty = 'id',
  optionLabelProp = 'name',
  isFavoriteProp = 'isFavorite',
  sortByProp,
  disableSorting,
  disableFavoriteFilter,
  disableClearable,
  disabled = false,
  value,
  type,
  renderInput,
  onChange,
  onShowMore,
  onClose,
  showMoreButton = true,
  showMoreText
}: FavoriteAutocompleteProps<T>) {
  const { t } = useTranslation('common');
  const { mutate: createFavorite } = mutations.useCreateFavorite();
  const { mutate: deleteFavorite } = mutations.useDeleteFavorite();
  const [filterOnFavorite, setFilterOnFavorite] = useState(
    !disableFavoriteFilter
  );
  const [selectedValue, setSelectedValue] = useState<
    AutocompleteOption<T> | null | undefined
  >(value);
  const [inputValue, setInputValue] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const isActivating = useRef(false);

  const sortBy = sortByProp || getLabelProp(optionLabelProp);
  const labelProps = useMemo(
    () =>
      typeof optionLabelProp === 'string' ? [optionLabelProp] : optionLabelProp,
    [optionLabelProp]
  );

  // Sync filterOnFavorite with disableFavoriteFilter prop changes
  useEffect(() => {
    setFilterOnFavorite(!disableFavoriteFilter);
  }, [disableFavoriteFilter]);

  const showMoreOption: ShowMoreOption = useMemo(
    () => ({
      id: 'showMore',
      name: showMoreText || t('showMore'),
      [idProperty]: 'showMore'
    }),
    [showMoreText, t, idProperty]
  );

  const noSuggestionsOption: NoSuggestionsOption = useMemo(
    () => ({
      id: 'noSuggestions',
      name: t('noSuggestions'),
      [idProperty]: 'noSuggestions'
    }),
    [t, idProperty]
  );

  const suggestionsLabelOption: SuggestionsLabelOption = useMemo(
    () => ({
      id: 'suggestionsLabel',
      name: t('suggestions'),
      [idProperty]: 'suggestionsLabel'
    }),
    [t, idProperty]
  );

  // Helper closures using imported utilities
  const isShowMore = useCallback(
    (o: AutocompleteOption<T> | null | undefined) =>
      isShowMoreOption(o, showMoreOption.id),
    [showMoreOption]
  );
  const isNoSuggestion = useCallback(
    (o: AutocompleteOption<T> | null | undefined) =>
      isNoSuggestionsOption(o, noSuggestionsOption.id),
    [noSuggestionsOption]
  );
  const isSuggestionsLabel = useCallback(
    (o: AutocompleteOption<T> | null | undefined) =>
      isSuggestionsLabelOption(o, suggestionsLabelOption.id),
    [suggestionsLabelOption]
  );

  function onOptionChange(
    event: React.SyntheticEvent,
    value: AutocompleteOption<T> | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<AutocompleteOption<T>>
  ) {
    if (isShowMore(value)) {
      setSelectedValue(null);
      isActivating.current = true;
      onShowMore?.();
    } else if (isNoSuggestion(value) || isSuggestionsLabel(value)) {
      return;
    } else {
      setSelectedValue(value);
      onChange?.(
        event,
        value as T,
        reason,
        details as AutocompleteChangeDetails<T>
      );
    }
  }

  function onInputChange(event: React.SyntheticEvent, value: string) {
    if (value !== showMoreOption.name) {
      setInputValue(value);
    }
  }

  function changeFavorite(option: T) {
    if (option[isFavoriteProp]) {
      deleteFavorite({
        referenceEntityId: option[entityIdProperty],
        referenceType: type
      });
    } else {
      createFavorite({
        referenceEntityId: option[entityIdProperty],
        referenceType: type
      });
    }
    option[isFavoriteProp] = !option[isFavoriteProp];
  }

  function FavoriteOption({
    props,
    option,
    isFavoriteProp,
    changeFavorite
  }: FavoriteOptionProps<T>) {
    const [isHover, setIsHover] = useState(false);
    const isLabelOption = isNoSuggestion(option) || isSuggestionsLabel(option);

    return (
      <li
        role={props.role}
        id={props.id}
        tabIndex={props.tabIndex}
        aria-selected={props['aria-selected']}
        aria-disabled={isLabelOption || props['aria-disabled']}
        data-option-index={props['data-option-index']}
        className={props.className}
        onClick={props.onClick}
        onMouseMove={props.onMouseMove}
        onTouchStart={props.onTouchStart}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        style={{
          ...(props.style || {}),
          ...(isLabelOption ? { pointerEvents: 'none', opacity: 0.6 } : {})
        }}
      >
        <OptionWrapper>
          {isShowMore(option) && (
            <ShowMoreOptionStyle>{option['name']}</ShowMoreOptionStyle>
          )}
          {isLabelOption && (
            <ShowMoreOptionStyle>{option['name']}</ShowMoreOptionStyle>
          )}
          {!isShowMore(option) && !isLabelOption && (
            <RegularOption>
              <OptionDisplayWrapper>
                {labelProps.map((prop) => (
                  <Box key={prop}>{option[prop]}</Box>
                ))}
              </OptionDisplayWrapper>
              <OptionIconWrapper>
                {(option[isFavoriteProp] || isHover) && (
                  <Icon
                    type={option[isFavoriteProp] ? 'star-fill' : 'star-outline'}
                    fontSize="inherit"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      changeFavorite(option as T);
                    }}
                  />
                )}
              </OptionIconWrapper>
            </RegularOption>
          )}
        </OptionWrapper>
      </li>
    );
  }

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AutocompleteOption<T>
  ) => {
    return (
      <FavoriteOption
        key={option[idProperty]}
        props={props}
        option={option}
        isFavoriteProp={isFavoriteProp}
        changeFavorite={changeFavorite}
      />
    );
  };

  const sortedOptions = useMemo(() => {
    const shouldRenderShowMore = showMoreButton && Boolean(onShowMore);
    const filtered = preFilterOptions(
      options,
      suggestedOptions,
      filterOnFavorite,
      entityIdProperty,
      idProperty,
      noSuggestionsOption,
      suggestionsLabelOption,
      showMoreOption,
      shouldRenderShowMore
    );
    return sortOptions(
      filtered,
      isFavoriteProp,
      sortBy,
      disableSorting,
      isShowMore,
      isNoSuggestion,
      isSuggestionsLabel
    );
  }, [
    options,
    suggestedOptions,
    disableSorting,
    filterOnFavorite,
    showMoreButton,
    onShowMore,
    entityIdProperty,
    idProperty,
    noSuggestionsOption,
    suggestionsLabelOption,
    showMoreOption,
    isFavoriteProp,
    sortBy,
    isShowMore,
    isNoSuggestion,
    isSuggestionsLabel
  ]);

  const filterOptions = useMemo(
    () =>
      createFilterOptions(
        labelProps,
        isShowMore,
        isNoSuggestion,
        isSuggestionsLabel
      ),
    [labelProps, isShowMore, isNoSuggestion, isSuggestionsLabel]
  );

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  return (
    <Autocomplete
      disabled={disabled}
      autoHighlight
      open={isOpen}
      renderInput={renderInput}
      options={sortedOptions}
      disableClearable={disableClearable}
      getOptionLabel={(option: AutocompleteOption<T>) =>
        option[getLabelProp(optionLabelProp)] || option['name']
      }
      isOptionEqualToValue={(
        a: AutocompleteOption<T>,
        b: AutocompleteOption<T>
      ) => a[idProperty] === b[idProperty]}
      value={selectedValue}
      inputValue={inputValue}
      filterOptions={filterOptions}
      noOptionsText={t('noOptions')}
      renderOption={renderOption}
      onChange={onOptionChange}
      onOpen={() => setIsOpen(true)}
      onInputChange={onInputChange}
      onClose={() => {
        const keepOpen = isActivating.current;
        setIsOpen(keepOpen);
        isActivating.current = false;
        if (!keepOpen) {
          onClose?.();
        }
      }}
    />
  );
}
